import React, { useCallback } from 'react';
import { Flex, FormControl, InputV2 } from '@withjoy/joykit';
import { startElementFilledWrapperStyles, startElementEmptyWrapperStyles, styles, RangeInputContainer } from './RangeInput.styles';

type RangeValue = {
  min: number | undefined;
  max: number | undefined;
};

type RangeInputOnChangeArgs = { name: 'min' | 'max'; value: number | undefined };

type RangeInputArgs = {
  unit?: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  value: RangeValue;
  minValue?: number;
  maxValue?: number;
  onChange?: (value: RangeValue) => void;
};

const RangeInput: React.FC<RangeInputArgs> = ({ unit, minLabel, maxLabel, minPlaceholder, maxPlaceholder, value, minValue, maxValue, onChange }) => {
  const inputMinRef = React.useRef<HTMLInputElement>(null);
  const inputMaxRef = React.useRef<HTMLInputElement>(null);

  const overrideInputWrapperStyles = (isFilled: boolean) => ({
    StartElement: {
      props: isFilled ? startElementFilledWrapperStyles : startElementEmptyWrapperStyles,
    },
  });
  const overrideFormControlStyles = {
    Label: {
      props: {
        typographyVariant: 'label3',
      },
    },
  };

  const handleChange = useCallback(() => {
    onChange?.({
      min: inputMinRef.current?.value ? Number(inputMinRef.current?.value) : undefined,
      max: inputMaxRef.current?.value ? Number(inputMaxRef.current?.value) : undefined,
    });
  }, [onChange]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-') {
      event.preventDefault(); // Cancel the native operation
    }
  }, []);

  return (
    <RangeInputContainer __css={styles.container}>
      <FormControl
        label={minLabel}
        overrides={overrideFormControlStyles}
      >
        <InputV2
          placeholder={minPlaceholder}
          type="number"
          min={minValue}
          max={maxValue}
          startElement={unit}
          value={value?.min ?? ''}
          name="min"
          onChange={handleChange}
          overrides={overrideInputWrapperStyles(!!value?.min)}
          onKeyPress={handleKeyPress}
          ref={inputMinRef}
          inputMode="numeric"
        />
      </FormControl>
      <Flex __css={styles.separator}> - </Flex>
      <FormControl
        label={maxLabel}
        overrides={overrideFormControlStyles}
      >
        <InputV2
          placeholder={maxPlaceholder}
          type="number"
          min={minValue}
          max={maxValue}
          startElement={unit}
          value={value?.max ?? ''}
          name="max"
          onChange={handleChange}
          overrides={overrideInputWrapperStyles(!!value?.max)}
          onKeyPress={handleKeyPress}
          ref={inputMaxRef}
          inputMode="numeric"
        />
      </FormControl>
    </RangeInputContainer>
  );
};

export { RangeInput };
export type { RangeValue, RangeInputArgs, RangeInputOnChangeArgs };
